import { useQuery } from '@tanstack/react-query';
import { InlineError, Skeleton } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * How fast each factory actually is, one row per supplier.
 *
 * ── Why legs and not a single lead time ────────────────────────────────────
 *
 * A single average hides the only useful thing: where the time goes. A factory
 * that acknowledges in a day and then sits on samples for three weeks needs a
 * different conversation from one that takes a week to acknowledge and then
 * flies — and averaged together they produce the same number and no
 * conversation at all.
 *
 * ── TAP's own column ───────────────────────────────────────────────────────
 *
 * "Us: reviewing samples" is on the table on purpose. A scorecard that only
 * measures the supplier invites "we were waiting on YOUR approval for nine
 * days", and the answer to that should be visible here rather than argued in
 * an email.
 */

export interface ScorecardRow {
  supplier_id: string;
  supplier_name: string;
  jobs: number;
  jobs_estimated: number;
  days_to_acknowledge: number | null;
  jobs_acknowledged: number;
  days_to_sample: number | null;
  jobs_sampled: number;
  days_tap_review: number | null;
  jobs_reviewed: number;
  days_to_ship: number | null;
  jobs_shipped: number;
  days_in_transit: number | null;
  jobs_delivered: number;
  days_end_to_end: number | null;
  jobs_completed: number;
  /** What the factory agreed to, averaged over the same jobs. */
  agreed_sample_days: number | null;
  agreed_production_days: number | null;
  /** Approved to READY FOR COLLECTION — the factory's own production time. */
  days_to_ready: number | null;
  jobs_ready: number;
}

/** One column of the table: what it measures, and whose leg it is. */
interface Leg {
  label: string;
  owner: 'Factory' | 'TAP' | 'Courier';
  days: (row: ScorecardRow) => number | null;
  jobs: (row: ScorecardRow) => number;
  /**
   * What they agreed to for this leg, when there is an agreement.
   *
   * Only two legs have one. A figure with nothing to compare it against is a
   * number; the same figure beside the promise is a conversation.
   */
  agreed?: (row: ScorecardRow) => number | null;
}

const LEGS: Leg[] = [
  {
    label: 'Accepting the PO',
    owner: 'Factory',
    days: (row) => row.days_to_acknowledge,
    jobs: (row) => row.jobs_acknowledged,
  },
  {
    label: 'Making samples',
    owner: 'Factory',
    days: (row) => row.days_to_sample,
    jobs: (row) => row.jobs_sampled,
    agreed: (row) => row.agreed_sample_days,
  },
  {
    label: 'Reviewing samples',
    owner: 'TAP',
    days: (row) => row.days_tap_review,
    jobs: (row) => row.jobs_reviewed,
  },
  {
    // To READY FOR COLLECTION, not to Shipped. What happens after ready is TAP
    // arranging a courier, and folding it in means the factory is right and we
    // are wrong the first time somebody raises it in a call.
    label: 'Building & ready',
    owner: 'Factory',
    days: (row) => row.days_to_ready,
    jobs: (row) => row.jobs_ready,
    agreed: (row) => row.agreed_production_days,
  },
  {
    label: 'Ready → shipped',
    owner: 'TAP',
    days: (row) =>
      row.days_to_ship === null || row.days_to_ready === null
        ? null
        : Math.round((row.days_to_ship - row.days_to_ready) * 10) / 10,
    jobs: (row) => row.jobs_shipped,
  },
  {
    label: 'In transit',
    owner: 'Courier',
    days: (row) => row.days_in_transit,
    jobs: (row) => row.jobs_delivered,
  },
  {
    label: 'PO to doorstep',
    owner: 'Factory',
    days: (row) => row.days_end_to_end,
    jobs: (row) => row.jobs_completed,
  },
];

async function fetchScorecard(client: SupabaseClient): Promise<ScorecardRow[]> {
  // No p_since: the generated type makes it a date string, and the whole
  // history is what a scorecard is for. A window belongs on the report below,
  // where the user picks it.
  const { data, error } = await client.rpc('cg_supplier_scorecard');
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

export function SupplierScorecard() {
  const { client } = useAuth();
  const scorecard = useQuery({
    queryKey: ['cg-supplier-scorecard'],
    queryFn: () => fetchScorecard(client),
    retry: false,
  });

  if (scorecard.isPending) {
    return <Skeleton lines={4} label="Loading supplier timings" />;
  }
  if (scorecard.isError) {
    return <InlineError>Supplier timings could not be loaded.</InlineError>;
  }

  const rows = scorecard.data ?? [];
  if (rows.length === 0) {
    return (
      <p className="job-order-muted">
        No timings yet. A supplier appears here once one of their jobs has moved through at least
        one stage.
      </p>
    );
  }

  const estimated = rows.reduce((sum, row) => sum + row.jobs_estimated, 0);

  return (
    <div>
      <table className="scorecard-table">
        <thead>
          <tr>
            <th scope="col">Supplier</th>
            {LEGS.map((leg) => (
              <th key={leg.label} scope="col">
                {leg.label}
                <span className="scorecard-owner">{leg.owner}</span>
              </th>
            ))}
            <th scope="col">Jobs</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.supplier_id}>
              <th scope="row">{row.supplier_name}</th>
              {LEGS.map((leg) => (
                <td key={leg.label}>
                  <Cell
                    days={leg.days(row)}
                    jobs={leg.jobs(row)}
                    agreed={leg.agreed === undefined ? null : leg.agreed(row)}
                  />
                </td>
              ))}
              <td>{row.jobs}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {estimated === 0 ? null : (
        <p className="scorecard-note">
          {estimated === 1 ? '1 job uses' : `${String(estimated)} jobs use`} dates reconstructed
          from before stage tracking existed. Those runs read longer than they were — treat them as
          a starting point, not a benchmark.
        </p>
      )}
    </div>
  );
}

/**
 * One figure, with the number of jobs behind it.
 *
 * The count is not decoration. "4.0 days" from two jobs and "4.0 days" from
 * forty are different claims, and a table that prints them identically will
 * have the first one quoted in a supplier conversation as though it were the
 * second.
 */
function Cell({
  days,
  jobs,
  agreed,
}: {
  days: number | null;
  jobs: number;
  agreed: number | null;
}) {
  if (days === null || jobs === 0) {
    // No job has finished this leg yet. An em dash rather than 0, because zero
    // days is a real and very different claim.
    return <span className="scorecard-empty">—</span>;
  }
  // Late by the agreed window, not by a judgement. The comparison is only
  // drawn where a promise exists — TAP's own legs and the courier's have none.
  const late = agreed !== null && days > agreed;
  return (
    <>
      <span className={late ? 'scorecard-days scorecard-days--late' : 'scorecard-days'}>
        {days} d
      </span>
      {agreed === null ? null : <span className="scorecard-agreed">vs {agreed} agreed</span>}
      <span className="scorecard-n">{jobs === 1 ? '1 job' : `${String(jobs)} jobs`}</span>
    </>
  );
}
